import { useState } from "react";
import api from "@/lib/api";
import { Layout } from "@/components/Layout";
import { toast } from "sonner";
import { Webhook, Plus, Trash2, Send, ArrowRight, Loader2 } from "lucide-react";

export default function ShopifySimulator() {
  const [orderId, setOrderId] = useState("");
  const [righe, setRighe] = useState([{ uid: crypto.randomUUID(), sku_completo: "TSA-BIA-S-VINT01", quantita: 3 }]);
  const [log, setLog] = useState([]);
  const [sending, setSending] = useState(false);

  const setRiga = (i, k, v) => setRighe((prev) => prev.map((r, idx) => (idx === i ? { ...r, [k]: v } : r)));
  const addRiga = () => setRighe((prev) => [...prev, { uid: crypto.randomUUID(), sku_completo: "", quantita: 1 }]);
  const delRiga = (i) => setRighe((prev) => prev.filter((_, idx) => idx !== i));

  const preview = (sku) => {
    const s = (sku || "").trim();
    const idx = s.lastIndexOf("-");
    if (idx === -1) return { neutro: s, grafica: "—" };
    return { neutro: s.slice(0, idx), grafica: s.slice(idx + 1) };
  };

  const send = async () => {
    const valid = righe.filter((r) => r.sku_completo.trim());
    if (valid.length === 0) { toast.error("Aggiungi almeno una riga valida"); return; }
    setSending(true);
    try {
      const { data } = await api.post("/ordini/simula", {
        id_ordine_shopify: orderId || null,
        righe: valid.map((r) => ({ sku_completo: r.sku_completo.trim(), quantita: Number(r.quantita) || 1 })),
      });
      const entry = { id: data.id_ordine_shopify, ts: new Date().toLocaleTimeString("it-IT"), operazioni: data.operazioni };
      setLog((prev) => [entry, ...prev].slice(0, 6));
      toast.success(`Ordine ${data.id_ordine_shopify} ricevuto e processato`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore nella simulazione dell'ordine");
    } finally {
      setSending(false);
    }
  };

  return (
    <Layout>
      <div className="mb-6">
        <div className="flex items-center gap-2 text-cyan-400 text-xs uppercase tracking-widest mb-1">
          <Webhook className="w-4 h-4" /> Integrazione tempo reale
        </div>
        <h1 className="font-heading text-2xl sm:text-3xl lg:text-4xl font-bold text-slate-100 tracking-tight">Simulatore Shopify Webhook</h1>
        <p className="text-slate-400 text-sm mt-1">Simula un evento «orders/create». Lo SKU esteso viene diviso all'ultimo trattino.</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        {/* Builder */}
        <div className="glass rounded-xl p-5">
          <h3 className="font-heading text-lg font-semibold text-slate-100 mb-4">Generatore Ordine</h3>
          <label className="text-xs text-slate-400 uppercase tracking-wider">ID Ordine Shopify (opzionale)</label>
          <input data-testid="order-id-input" value={orderId} onChange={(e) => setOrderId(e.target.value)} placeholder="Auto-generato se vuoto"
            className="mt-1 mb-4 w-full bg-slate-900/60 border border-slate-700 rounded-lg px-3 py-2 text-slate-100 focus:outline-none focus:border-cyan-400/60 text-sm" />

          <div className="space-y-3">
            {righe.map((r, i) => {
              const p = preview(r.sku_completo);
              return (
                <div key={r.uid} className="bg-slate-900/40 border border-slate-700/60 rounded-lg p-3" data-testid={`order-line-${i}`}>
                  <div className="flex gap-2 items-start">
                    <div className="flex-1">
                      <input value={r.sku_completo} onChange={(e) => setRiga(i, "sku_completo", e.target.value)} placeholder="SKU Completo (es. TSA-BIA-S-VINT01)" data-testid={`line-sku-${i}`}
                        className="w-full bg-slate-900/70 border border-slate-700 rounded-md px-3 py-2 text-slate-100 font-mono-tech text-sm focus:outline-none focus:border-cyan-400/60" />
                    </div>
                    <input type="number" min="1" value={r.quantita} onChange={(e) => setRiga(i, "quantita", e.target.value)} data-testid={`line-qty-${i}`}
                      className="w-20 bg-slate-900/70 border border-slate-700 rounded-md px-3 py-2 text-slate-100 text-sm focus:outline-none focus:border-cyan-400/60" />
                    <button onClick={() => delRiga(i)} className="w-9 h-9 rounded-md bg-red-500/15 text-red-300 hover:bg-red-500/25 flex items-center justify-center shrink-0"><Trash2 className="w-4 h-4" /></button>
                  </div>
                  {r.sku_completo.trim() && (
                    <div className="flex items-center gap-2 mt-2 text-xs">
                      <span className="text-slate-500">Split:</span>
                      <span className="font-mono-tech text-cyan-300 bg-cyan-400/10 px-1.5 py-0.5 rounded">{p.neutro}</span>
                      <ArrowRight className="w-3 h-3 text-slate-600" />
                      <span className="font-mono-tech text-amber-300 bg-amber-400/10 px-1.5 py-0.5 rounded">Grafica: {p.grafica}</span>
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          <button onClick={addRiga} data-testid="add-line-btn" className="mt-3 flex items-center gap-2 text-sm text-cyan-300 hover:text-cyan-200">
            <Plus className="w-4 h-4" /> Aggiungi riga
          </button>

          <button data-testid="webhook-simulate-btn" onClick={send} disabled={sending}
            className="mt-5 w-full bg-cyan-400 hover:bg-cyan-300 text-slate-900 font-semibold rounded-lg py-3 flex items-center justify-center gap-2 disabled:opacity-60">
            {sending ? <Loader2 className="w-5 h-5 animate-spin" /> : <Send className="w-5 h-5" />} Invia Ordine Simulato
          </button>

          <div className="mt-4 text-xs text-slate-500 bg-slate-900/40 rounded-lg p-3 border border-slate-700/60">
            <div className="font-semibold text-slate-400 mb-1">Endpoint Webhook reale</div>
            <code className="font-mono-tech text-cyan-400 break-all">POST {process.env.REACT_APP_BACKEND_URL}/api/shopify/webhook</code>
          </div>
        </div>

        {/* Event log */}
        <div className="glass rounded-xl p-5">
          <h3 className="font-heading text-lg font-semibold text-slate-100 mb-4">Log Eventi Webhook</h3>
          {log.length === 0 ? (
            <div className="text-slate-500 text-sm py-8 text-center">Nessun evento ricevuto. Invia un ordine per vedere la logica di split.</div>
          ) : (
            <div className="space-y-3" data-testid="webhook-log">
              {log.map((e, i) => (
                <div key={`${e.id}-${e.ts}`} className="bg-slate-900/40 border border-slate-700/60 rounded-lg p-3 fade-in">
                  <div className="flex items-center justify-between mb-2">
                    <span className="font-mono-tech text-cyan-300 text-sm">#{e.id}</span>
                    <span className="text-xs text-slate-500">{e.ts}</span>
                  </div>
                  {e.operazioni.map((op, j) => (
                    <div key={`${op.sku_completo}-${j}`} className="text-xs py-1 border-t border-slate-800/60 first:border-0">
                      <span className="font-mono-tech text-slate-300">{op.sku_completo}</span>
                      <span className="text-slate-600"> → </span>
                      <span className="font-mono-tech text-cyan-400">{op.sku_neutro}</span>
                      <span className="text-slate-600"> + </span>
                      <span className="font-mono-tech text-amber-400">{op.id_grafica}</span>
                      <span className="text-slate-500"> · qty {op.quantita} · </span>
                      {op.esito === "scalato" ? (
                        <span className={op.sotto_soglia ? "text-red-400 font-semibold" : "text-green-400"}>
                          residuo {op.quantita_residua}{op.sotto_soglia ? " ⚠ Sotto Scorta" : ""}
                        </span>
                      ) : (
                        <span className="text-red-400 font-semibold">SKU neutro non trovato</span>
                      )}
                    </div>
                  ))}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </Layout>
  );
}
